import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Download, FileSpreadsheet, Loader2, Check, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const PickSlipUpload = () => {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [errors, setErrors] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [created, setCreated] = useState([]);

  const handleFile = async (file) => {
    if (!file) return;
    setParsing(true); setErrors([]); setPreview(null); setCreated([]);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await apiClient.post('/sample/csv-upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      setErrors(res.data.errors || []);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail.errors) setErrors(detail.errors);
      else toast.error(detail || 'Upload failed');
    } finally { setParsing(false); }
  };

  const generate = async () => {
    if (!preview) return;
    setGenerating(true);
    try {
      const res = await apiClient.post('/sample/generate-slips', {
        consolidated: preview.consolidated,
      });
      setCreated(res.data.created || []);
      toast.success(`${res.data.count} pick slip(s) created`);
      setPreview(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Slip generation failed');
    } finally { setGenerating(false); }
  };

  const downloadTemplate = async () => {
    const res = await apiClient.get('/sample/csv-template');
    const blob = new Blob([res.data.template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'sample_pick_slip_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="sample-pickslip-upload">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/sections/sample')}
              className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Sample Pick Slip — CSV Upload</h1>
              <p className="text-sm text-[#E8DCC8]">FULL & SWATCH samples for agents and conferences</p>
            </div>
          </div>
          <Button variant="secondary" onClick={downloadTemplate}
            data-testid="download-template-btn">
            <Download className="w-4 h-4 mr-2" /> Template
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FileSpreadsheet className="w-4 h-4" /> Upload CSV
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-gray-600">
              Required columns: <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">
                sample_type, agent_name, conference_place, sku_or_barcode, qty, remarks
              </code>.
              Each row needs <strong>either</strong> agent_name or conference_place (not both).
              Pick slips are auto-capped at 120 pcs each.
            </p>
            <div className="flex items-center gap-2">
              <input type="file" ref={fileRef} accept=".csv" hidden
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="csv-file-input" />
              <Button onClick={() => fileRef.current?.click()} disabled={parsing}
                className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                data-testid="csv-upload-btn">
                {parsing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Choose CSV
              </Button>
            </div>
            {errors.length > 0 && (
              <div className="rounded border border-red-200 bg-red-50 p-3 text-sm">
                <div className="flex items-center gap-2 text-red-700 font-medium mb-1">
                  <AlertTriangle className="w-4 h-4" /> {errors.length} error(s) in CSV
                </div>
                <ul className="text-red-700 text-xs space-y-0.5">
                  {errors.slice(0, 20).map((e, i) => <li key={i}>• {e}</li>)}
                  {errors.length > 20 && <li>… and {errors.length - 20} more</li>}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>

        {preview && (
          <Card data-testid="preview-card">
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-base">Preview</CardTitle>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline">Rows {preview.row_count}</Badge>
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">
                    Full {preview.full_qty} pcs
                  </Badge>
                  <Badge className="bg-blue-100 text-blue-700 border-blue-300">
                    Swatch {preview.swatch_qty} pcs
                  </Badge>
                  <Badge>~{preview.estimated_slips} slip(s)</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>SKU / Barcode</TableHead>
                    <TableHead>Total Qty</TableHead>
                    <TableHead>Destinations</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.consolidated.map((c, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Badge variant={c.sample_type === 'FULL' ? 'default' : 'secondary'}>
                          {c.sample_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{c.sku_or_barcode}</TableCell>
                      <TableCell><strong>{c.total_qty}</strong></TableCell>
                      <TableCell className="text-xs">
                        {c.destinations.map((d, j) => (
                          <div key={j} className="text-gray-600">
                            {d.agent_name || d.conference_place} × {d.qty}
                            {d.remarks && <span className="text-gray-400"> — {d.remarks}</span>}
                          </div>
                        ))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex justify-end mt-4">
                <Button onClick={generate} disabled={generating}
                  className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                  data-testid="generate-slips-btn">
                  {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  Generate Pick Slips
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {created.length > 0 && (
          <Card data-testid="created-slips-card">
            <CardHeader>
              <CardTitle className="text-base">Pick Slips Created</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Slip #</TableHead>
                    <TableHead>Sample Type</TableHead>
                    <TableHead>Total Qty</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {created.map((c) => (
                    <TableRow key={c.slip_id}>
                      <TableCell className="font-mono text-xs">{c.pick_slip_number}</TableCell>
                      <TableCell>
                        <Badge variant={c.sample_type === 'FULL' ? 'default' : 'secondary'}>
                          {c.sample_type}
                        </Badge>
                      </TableCell>
                      <TableCell>{c.total_qty}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default PickSlipUpload;
